import { z } from 'zod';
import type { LocalizedText, PermissionDefinition } from '../../permissions.ts';

/** Módulo Defeitos: permissões, validações e tipos do cadastro de bugs da linha de produção. */

export const PERMISSION_MODULES = {
  defects: { 'pt-BR': 'Defeitos', en: 'Defects' },
} satisfies Record<string, LocalizedText>;

export const PERMISSIONS = [
  {
    code: 'defects.defect.read',
    module: 'defects',
    label: { 'pt-BR': 'Ver defeitos', en: 'View defects' },
  },
  {
    code: 'defects.defect.manage',
    module: 'defects',
    label: {
      'pt-BR': 'Registrar e alterar defeitos',
      en: 'Record and edit defects',
    },
  },
] as const satisfies readonly PermissionDefinition[];

export const DEFECT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type DefectSeverity = (typeof DEFECT_SEVERITIES)[number];

export const DEFECT_STATUSES = ['open', 'investigating', 'resolved'] as const;
export type DefectStatus = (typeof DEFECT_STATUSES)[number];

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => s || null)
    .nullable()
    .optional()
    .transform((s) => s ?? null);

export const defectInput = z.object({
  title: z.string().trim().min(1).max(200),
  description: optionalText(4000),
  /** Em que etapa da linha de produção apareceu (impressão, limpeza, pintura...). */
  stage: optionalText(120),
  severity: z.enum(DEFECT_SEVERITIES).default('medium'),
  status: z.enum(DEFECT_STATUSES).default('open'),
  /** Dia em que foi detectado (AAAA-MM-DD). */
  detectedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  resolution: optionalText(4000),
});
export type DefectInput = z.input<typeof defectInput>;

/** O defeito como a API devolve. */
export interface Defect {
  id: string;
  title: string;
  description: string | null;
  stage: string | null;
  severity: DefectSeverity;
  status: DefectStatus;
  detectedOn: string;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
}
